import React from 'react';
import {View} from 'react-native';
import {useSelector} from 'react-redux';
import {get} from 'lodash';

import Text from '/component/base/Text';
import {DashboardID} from '/constant/TestIDs';
import {DoubleDoubleEndDate, DoubleDoubleStartDate} from '/enum/dates.enum';
import {useTranslation} from '/provider/TranslationProvider';
import {
  variablesBannerText,
  variablesFeatureFlag,
} from '/redux/selector/remoteConfig.selector';
import {getDateUtilDefaultInstance} from '/util/date.util';
import {shouldReleaseFeature} from '/util/featureRelease.util';
import testProperties from '/util/testing/testProperties';

import {styles} from './BonusProgressBar.style';
import {getDaysLeftForBonus} from './BonusProgressBar.util';
import {getEarnedBonusAmount} from './BonusProgressBar.util';
import {OEBox} from './OEBox';
interface BonusProgressBarProps {
  newOrderingEntities?: number;
  currentDate?: string;
  isBanner?: boolean;
  earnedBonusAmount?: number | null;
  isEarnedBonusCard?: boolean;
  bonusData?: Record<string, any> | null;
  doubleDoubleBonusPeriod?: {
    DoubleDoubleStartDate?: string;
    DoubleDoubleEndDate?: string;
  } | null;
  currentBonusEndDate: string;
}

export const BonusProgressBar: React.FC<BonusProgressBarProps> = ({
  newOrderingEntities = 0,
  currentDate = '',
  isBanner = false,
  earnedBonusAmount = null,
  isEarnedBonusCard = false,
  bonusData = null,
  doubleDoubleBonusPeriod = null,
  currentBonusEndDate = '',
}: any) => {
  let parsedPeriod: any = {};
  try {
    parsedPeriod = JSON.parse(doubleDoubleBonusPeriod) || {};
  } catch {
    parsedPeriod =
      typeof doubleDoubleBonusPeriod === 'object' &&
      doubleDoubleBonusPeriod != null
        ? doubleDoubleBonusPeriod
        : {};
  }
  const {
    DoubleDoubleStartDate: DDBStartDate = DoubleDoubleStartDate,
    DoubleDoubleEndDate: DDBEndDate = DoubleDoubleEndDate,
  } = parsedPeriod;

  const {ts, tt} = useTranslation('bonusProgressBar');
  const dateUtil = getDateUtilDefaultInstance();
  const currentDateFormatted = dateUtil.formatDate(currentDate, 'MMMM yyyy');
  const VariablesFeatureFlag = useSelector(variablesFeatureFlag);
  const showBusinessPrograms = shouldReleaseFeature(
    get(VariablesFeatureFlag, 'ff_hide_business_programs', ''),
  );

  const isDoublePeriod = dateUtil.isWithinInterval(
    currentDate,
    DDBStartDate,
    DDBEndDate,
  );

  const isWithinTwoMonthsAfterDDBEndDate = dateUtil.isDateWithinTwoMonthsPeriod(
    DDBEndDate,
    currentBonusEndDate,
  );

  const daysLeftForBonus = getDaysLeftForBonus({currentDate});

  const intermediateAmount =
    isDoublePeriod || isWithinTwoMonthsAfterDDBEndDate
      ? get(bonusData, 'CABV2Text.intermediateAmount', 600)
      : get(bonusData, 'CABText.intermediateAmount', 150);

  const maxAmount =
    isDoublePeriod || isWithinTwoMonthsAfterDDBEndDate
      ? get(bonusData, 'CABV2Text.maxAmount', 1000)
      : get(bonusData, 'CABText.maxAmount', 250);

  // Determine earnedMaxAmount based on earnedBonusAmount
  let earnedMaxAmount = maxAmount;

  // Temporarily using a max bonus value of 1000 as per the latest requirements.
  // Keeping the logic below for future use in case bonus values change again.
  /* if (earnedBonusAmount >= 150 && earnedBonusAmount <= 250) {
    earnedMaxAmount = 250;
  } else if (earnedBonusAmount >= 600 && earnedBonusAmount <= 1000) {
    earnedMaxAmount = 1000;
  } */

  const finalMaxAmount = isEarnedBonusCard ? earnedMaxAmount : maxAmount;

  //calculate how much money coach got based on the number of ordering entities
  const earnedBonus = getEarnedBonusAmount({
    newOrderingEntities,
    intermediateAmount,
    maxAmount,
  });
  const featureFlag = useSelector(variablesBannerText);
  const pqvText =
    isDoublePeriod || isWithinTwoMonthsAfterDDBEndDate
      ? get(
          bonusData,
          'CABV2Text.PQVText',
          'New Clients with at least 275 PQV:',
        )
      : featureFlag?.PQVText ?? 'New Clients with at least 150 PQV:';
  const bonusAmount = isEarnedBonusCard ? earnedBonusAmount : earnedBonus;
  const boxesArray: any = [];
  for (let i = 0; i < 5; i++) {
    i < newOrderingEntities
      ? boxesArray.push({isChecked: true})
      : boxesArray.push({isChecked: false});
  }
  const checkedCount = boxesArray.filter(box => box.isChecked).length;

  const renderBonusAmountPerOE = () => (
    <View style={styles.emptyBoxesContainer}>
      {boxesArray.map((item, index) => {
        if (index === 2) {
          return (
            <View
              style={styles.emptyBox}
              key={index}
              accessible={true}
              accessibilityRole="text"
              accessibilityLabel={`Bonus amount for 3 new clients: $${intermediateAmount}`}>
              <Text allowFontScaling maxFontSizeMultiplier={2}>
                ${intermediateAmount}
              </Text>
            </View>
          );
        }
        if (index === 4) {
          return (
            <View
              style={styles.emptyBox}
              key={index}
              accessible={true}
              accessibilityRole="text"
              accessibilityLabel={`Bonus amount for 5 new clients: $${finalMaxAmount}`}>
              <Text allowFontScaling maxFontSizeMultiplier={2}>
                ${finalMaxAmount}
              </Text>
            </View>
          );
        }
        return <View style={styles.emptyBox} key={index} />;
      })}
    </View>
  );

  const renderDaysLeft = () => {
    if (daysLeftForBonus > 0) {
      return tt('daysLeft', () => (
        <Text
          allowFontScaling
          maxFontSizeMultiplier={2}
          accessible={true}
          accessibilityRole="text"
          accessibilityLabel={`${daysLeftForBonus} days left`}
          style={styles.daysLeft}
          emphasis="medium"
          variant="caption"
          {...testProperties(DashboardID.dashboardProgressBarDaysLeft)}>
          {daysLeftForBonus} days left
        </Text>
      ));
    }

    return tt('lastDay', () => (
      <Text
        allowFontScaling
        maxFontSizeMultiplier={2}
        style={styles.daysLeft}
        emphasis="medium"
        variant="caption"
        {...testProperties(DashboardID.dashboardProgressBarDaysLeft)}
        accessible={true}
        accessibilityRole="text"
        accessibilityLabel="Last day">
        Last day
      </Text>
    ));
  };

  return (
    <View>
      <View style={styles.heading} accessible={false}>
        <View
          style={styles.subHeading}
          accessible={true}
          accessibilityLabel={
            showBusinessPrograms
              ? `Current date: ${currentDateFormatted}. ${daysLeftForBonus} days left.`
              : `Current date: ${currentDateFormatted}. ${daysLeftForBonus} days left. Bonus: ${bonusAmount} of ${finalMaxAmount}`
          }>
          <Text
            allowFontScaling
            maxFontSizeMultiplier={2}
            variant={isBanner ? 'heading' : 'small'}
            {...testProperties(DashboardID.dashboardProgressBarCurrentDate)}
            accessible={false}>
            {currentDateFormatted}
          </Text>
          {!isEarnedBonusCard && renderDaysLeft()}
        </View>
        {isEarnedBonusCard &&
          tt('moneyEarned', () => (
            <Text
              allowFontScaling
              maxFontSizeMultiplier={2}
              {...testProperties(DashboardID.dashboardProgressBarEarned)}
              accessible={true}>
              <Text
                allowFontScaling
                maxFontSizeMultiplier={2}
                style={styles.moneyBold}
                accessible={true}>
                ${bonusAmount}
              </Text>
              {!showBusinessPrograms && `  of ${finalMaxAmount}`}
            </Text>
          ))}
      </View>
      {!isEarnedBonusCard && (
        <>
          <View
            accessible={true}
            accessibilityLabel={`${checkedCount} ${pqvText}`}>
            <Text
              allowFontScaling
              maxFontSizeMultiplier={2}
              variant="caption"
              style={styles.boxTitle}
              {...testProperties(DashboardID.dashboardProgressBarLabel)}
              accessible={true}
              accessibilityLabel={`${checkedCount} ${pqvText}`}>
              {ts('newClientsWith', pqvText)}
            </Text>
          </View>
          <View style={styles.boxesContainer}>
            {boxesArray.map(({isChecked}, index) => (
              <OEBox isChecked={isChecked} number={index + 1} key={index} />
            ))}
          </View>
          {!showBusinessPrograms && renderBonusAmountPerOE()}
        </>
      )}
    </View>
  );
};
